import { indexById, isValueExist } from '@/utils/utils';
import * as S from './styles';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { IFRAME_EVENT_MESSAGE } from '@consts';
import { ActionButton } from '@/components/Common/Dialog';
import withHMR from '@/utils/withHMR';
import SearchIcon from '@svgIcons/search.svg';
import PlusIcon from '@svgIcons/plus.svg';
import SecondPanel from '@/components/Dashboard/common/WidgetSettingPanel/options/common/secondPanel';
import RingLoader from '@/components/Common/RingLoader';
import WithDropdown from '@/components/Common/WithDropdown';
import {
    normalizeFilterWidgetAvailableLabel,
    isFilterWidgetBreakdownLabel,
} from '@/components/Dashboard/common/WidgetSettingPanel/utils';

const MAX_SELECTED_LABELS = 10000;

const SecondPanelContent = {
    name: 'SecondPanelContent',
    props: {
        handleCancel: {
            type: Function,
            default: () => {},
        },
        handleSubmit: {
            type: Function,
            default: () => {},
        },
        handleUpdateAvailableLabels: {
            type: Function,
            default: () => {},
        },
        availableLabels: {
            type: Array,
            default: () => [],
        },
        breakdownList: {
            type: Array,
            default: () => [],
        },
        breakdownLabelsById: {
            type: Object,
            default: () => ({}),
        },
        allBreakdownLabelsById: {
            type: Object,
            default: () => ({}),
        },
        breakdownLabelLoadedMap: {
            type: Object,
            default: () => ({}),
        },
        breakdownLabelLoadingMap: {
            type: Object,
            default: () => ({}),
        },
        categoryCheckStateById: {
            type: Object,
            default: () => ({}),
        },
        handleFetchBreakdownLabels: {
            type: Function,
            default: () => {},
        },
        searchQuery: {
            type: String,
            default: '',
        },
        isSearchLoading: {
            type: Boolean,
            default: false,
        },
        handleSearchQueryChange: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {};
    },
    watch: {
        searchQuery: {
            handler(nextValue, prevValue) {
                const prev = prevValue?.trim() || '';
                const next = nextValue?.trim() || '';
                if (prev && !next) {
                    this.$refs.secondPanel?.collapseAllCategories?.();
                }
            },
        },
    },
    computed: {
        ...mapGetters('user', ['isLimit10000CustomBreakdownEnabled']),
        ...mapGetters('newDashboard', ['currentView']),
        isTemplateView() {
            return this.currentView?.isTemplate || false;
        },
        normalizedAvailableLabels() {
            return (this.availableLabels || []).map(
                normalizeFilterWidgetAvailableLabel
            );
        },
        availableLabelIdsSet() {
            return new Set(
                this.normalizedAvailableLabels
                    .filter((label) => !isFilterWidgetBreakdownLabel(label))
                    .map((label) => label.id)
            );
        },
        selectedBreakdownIdsSet() {
            return new Set(
                this.normalizedAvailableLabels
                    .filter(isFilterWidgetBreakdownLabel)
                    .map((label) => String(label.id))
            );
        },
        maxSelectedLabels() {
            return MAX_SELECTED_LABELS;
        },
        breakdownById() {
            return indexById(this.breakdownList || []);
        },
        emptyCategoryRefreshToken() {
            const loadedCount = Object.keys(
                this.breakdownLabelLoadedMap || {}
            ).length;
            const loadingMap = this.breakdownLabelLoadingMap || {};
            const loadingCount = Object.keys(loadingMap).length;
            const labelCount = Object.keys(
                this.breakdownLabelsById || {}
            ).length;
            const loadingStateToken = Object.entries(loadingMap)
                .map(([key, value]) => `${key}:${value === true ? '1' : '0'}`)
                .join('|');
            return `${loadedCount}-${loadingCount}-${labelCount}-${loadingStateToken}`;
        },
        normalizedSearchQuery() {
            return this.searchQuery?.trim() || '';
        },
        isSearchActive() {
            return Boolean(this.normalizedSearchQuery);
        },
        loopTarget() {
            return (this.breakdownList || []).map((breakdown) => [
                String(breakdown.id),
                [],
            ]);
        },
        currentList() {
            const {
                breakdownList,
                availableLabelIdsSet,
                selectedBreakdownIdsSet,
            } = this;

            return (breakdownList || []).flatMap((breakdown) => {
                const labels = this.getBreakdownLabels(String(breakdown.id));

                return labels.map((label) => ({
                    ...label,
                    category: String(breakdown.id),
                    entryKey: label.id,
                    selected:
                        selectedBreakdownIdsSet.has(String(breakdown.id)) ||
                        availableLabelIdsSet.has(label.id),
                    isBlock: selectedBreakdownIdsSet.has(String(breakdown.id)),
                }));
            });
        },
    },
    methods: {
        ...mapActions('iframe', ['postIframeMessage']),
        ...mapMutations('newDashboard', ['setCustomBreakdownPanelData']),
        updateSearchQuery(value) {
            this.handleSearchQueryChange(value);
        },
        getBreakdownLabels(customBreakdownId) {
            const key = String(customBreakdownId);
            return this.breakdownLabelsById?.[key] || [];
        },
        isSelectionLimitReached() {
            return (
                (this.availableLabels?.length || 0) >= this.maxSelectedLabels
            );
        },
        toggleLabel(label) {
            const {
                availableLabelIdsSet,
                availableLabels,
                handleUpdateAvailableLabels,
                selectedBreakdownIdsSet,
            } = this;

            if (selectedBreakdownIdsSet.has(String(label.customBreakdownId))) {
                return;
            }

            if (availableLabelIdsSet.has(label.id)) {
                handleUpdateAvailableLabels(
                    this.normalizedAvailableLabels.filter(
                        (l) =>
                            !(
                                !isFilterWidgetBreakdownLabel(l) &&
                                l.id === label.id
                            )
                    )
                );
            } else if (this.isSelectionLimitReached()) {
                return;
            } else {
                handleUpdateAvailableLabels([
                    ...(availableLabels || []),
                    {
                        type: 'label',
                        id: label.id,
                        customBreakdownId: label.customBreakdownId,
                    },
                ]);
            }
        },
        async handleBulkToggleLabels(
            _,
            allChecked,
            targetLabelIds = [],
            category = null
        ) {
            if (category !== null && category !== 'all') {
                const breakdownKey = String(category);
                const normalizedLabels = this.normalizedAvailableLabels;
                let updatedLabels;

                if (
                    this.selectedBreakdownIdsSet.has(breakdownKey) ||
                    allChecked
                ) {
                    updatedLabels = normalizedLabels.filter(
                        (label) =>
                            !(
                                isFilterWidgetBreakdownLabel(label) &&
                                String(label.id) === breakdownKey
                            )
                    );
                } else {
                    updatedLabels = normalizedLabels.filter((label) => {
                        if (isFilterWidgetBreakdownLabel(label)) {
                            return true;
                        }

                        return String(label.customBreakdownId) !== breakdownKey;
                    });
                    updatedLabels.push({
                        type: 'breakdown',
                        id: Number.isNaN(Number(category))
                            ? category
                            : Number(category),
                    });
                }

                this.handleUpdateAvailableLabels(updatedLabels);
                return;
            }

            // Intentional behavior: when search is active, this category-level
            // toggle applies to the currently visible (filtered) rows only.
            // Clearing search reveals the full category's actual selection state.
            let resolvedTargetLabelIds = Array.isArray(targetLabelIds)
                ? targetLabelIds
                : [];
            let fallbackLabelById = new Map();

            if (
                resolvedTargetLabelIds.length === 0 &&
                !allChecked &&
                category !== null &&
                category !== 'all'
            ) {
                const breakdownId = Number(category);
                const breakdownKey = String(category);
                const isAlreadyLoaded =
                    this.breakdownLabelLoadedMap?.[breakdownKey] === true;

                const getLabelIds = () =>
                    (this.allBreakdownLabelsById?.[breakdownKey] || [])
                        .map((label) => label?.id)
                        .filter(isValueExist);

                if (!isAlreadyLoaded && !Number.isNaN(breakdownId)) {
                    const result = await this.handleFetchBreakdownLabels(
                        breakdownId
                    );
                    const fetchedLabels = Array.isArray(result?.labels)
                        ? result.labels
                        : [];
                    const fetchedLabelIds = fetchedLabels
                        .map((label) => label?.id)
                        .filter(isValueExist);
                    if (fetchedLabelIds.length > 0) {
                        resolvedTargetLabelIds = fetchedLabelIds;
                        fallbackLabelById = new Map(
                            fetchedLabels
                                .filter((label) => isValueExist(label?.id))
                                .map((label) => [label.id, label])
                        );
                    }
                }

                if (resolvedTargetLabelIds.length === 0) {
                    resolvedTargetLabelIds = getLabelIds();
                }
            }

            if (
                !Array.isArray(resolvedTargetLabelIds) ||
                resolvedTargetLabelIds.length === 0
            ) {
                return;
            }

            const selectedMap = new Map(
                this.normalizedAvailableLabels.map((label) => [
                    `${label.type}:${label.id}`,
                    label,
                ])
            );

            if (allChecked) {
                resolvedTargetLabelIds.forEach((id) => {
                    selectedMap.delete(`label:${id}`);
                });

                this.handleUpdateAvailableLabels(
                    Array.from(selectedMap.values())
                );
                return;
            }

            const labelById = new Map(
                this.currentList.map((label) => [label.id, label])
            );
            const selectedCount = selectedMap.size;
            const remainingSlots = this.maxSelectedLabels - selectedCount;

            if (remainingSlots <= 0) {
                return;
            }

            resolvedTargetLabelIds
                .filter((id) => !selectedMap.has(`label:${id}`))
                .slice(0, remainingSlots)
                .forEach((id) => {
                    const label =
                        labelById.get(id) || fallbackLabelById.get(id);
                    const fallbackBreakdownId =
                        category !== null &&
                        category !== 'all' &&
                        !Number.isNaN(Number(category))
                            ? Number(category)
                            : null;

                    selectedMap.set(`label:${id}`, {
                        type: 'label',
                        id,
                        customBreakdownId:
                            label?.customBreakdownId ?? fallbackBreakdownId,
                    });
                });

            this.handleUpdateAvailableLabels(Array.from(selectedMap.values()));
        },
        getCategory(label) {
            return label.category;
        },
        getCategoryName(category) {
            return this.breakdownById?.[category]?.name || String(category);
        },
        getCategoryCheckState(category) {
            return this.categoryCheckStateById?.[String(category)];
        },
        getCategoryBadge(category) {
            const categoryKey = String(category);
            const labels = this.breakdownLabelsById?.[categoryKey];
            if (Array.isArray(labels) && labels.length > 0) {
                return labels.length;
            }

            const breakdown =
                this.breakdownById?.[categoryKey] ||
                this.breakdownById?.[category];
            return breakdown?.labelsCount;
        },
        renderCategoryContent({ category, text, renderCategoryIcon }) {
            const categoryBadge = this.getCategoryBadge(category);

            return (
                <S.CategoryContent>
                    {renderCategoryIcon()}
                    <S.CategoryText class="vue-line-clamp">
                        {text}
                    </S.CategoryText>
                    {isValueExist(categoryBadge) && (
                        <S.CategoryBadge>{categoryBadge}</S.CategoryBadge>
                    )}
                </S.CategoryContent>
            );
        },
        resolveCategoryChecked({ category }) {
            return this.getCategoryCheckState(category) === 'checked';
        },
        resolveCategoryIndeterminate({ category }) {
            return this.getCategoryCheckState(category) === 'indeterminate';
        },
        handleCategoryToggle({ category, isClosed }) {
            const labelKey = String(category);
            if (isClosed) {
                return;
            }
            if (
                this.breakdownLabelLoadedMap?.[labelKey] ||
                this.breakdownLabelLoadingMap?.[labelKey]
            ) {
                return;
            }

            const breakdownId = Number(labelKey);
            if (Number.isNaN(breakdownId)) {
                this.handleFetchBreakdownLabels(labelKey);
                return;
            }

            this.handleFetchBreakdownLabels(breakdownId);
        },
        handleEditBreakdown(category) {
            const breakdownId = Number(category);
            const breakdown = this.breakdownById?.[category];
            if (!breakdown) {
                return;
            }

            const data = {
                breakdownEditId: breakdownId,
                breakdownEditWidgetAssetIds: [],
                mode: null,
                isTemplateView: this.isTemplateView,
                currentView: this.currentView,
            };

            this.setCustomBreakdownPanelData(data);
            this.postIframeMessage({
                type: IFRAME_EVENT_MESSAGE.TOGGLE_CUSTOM_BREAKDOWN_PANEL,
                origin: 'VUE',
                data,
            });
        },
        resolveEmptyCategoryMessage({ category }) {
            const labelKey = String(category);
            const labelsMap = this.breakdownLabelsById || {};
            const labels = labelsMap[labelKey];
            const loadingMap = this.breakdownLabelLoadingMap || {};
            const hasLoadingEntry = Object.prototype.hasOwnProperty.call(
                loadingMap,
                labelKey
            );
            const isLoading = loadingMap[labelKey] === true;
            const isFetchCompleted = hasLoadingEntry && !isLoading;

            if (!isFetchCompleted) {
                return null;
            }

            if (Array.isArray(labels)) {
                return labels.length === 0
                    ? this.$t(
                          'NEW_DASHBOARD.dropdown_options_panel.no_breakdown_labels'
                      )
                    : null;
            }

            return this.$t(
                'NEW_DASHBOARD.dropdown_options_panel.no_breakdown_labels'
            );
        },
        renderSearchInput() {
            return (
                <S.SearchInputWrapper>
                    <S.SearchInput
                        placeholder={this.$t('COMMON.search')}
                        onInput={this.updateSearchQuery}
                        value={this.searchQuery}
                    />
                    <S.SearchIconWrapper>
                        <SearchIcon
                            width={16}
                            height={16}
                            v-view-box
                            fill="#BDC2C7"
                        />
                    </S.SearchIconWrapper>
                </S.SearchInputWrapper>
            );
        },
        renderSecondPanelList() {
            const { currentList, loopTarget } = this;

            if (this.isSearchActive && this.isSearchLoading) {
                return (
                    <S.NoResultWrapper>
                        <RingLoader size={20} />
                    </S.NoResultWrapper>
                );
            }

            if (!isValueExist(loopTarget)) {
                return (
                    <S.NoResultWrapper>
                        {this.$t(
                            'NEW_DASHBOARD.dropdown_options_panel.no_labels'
                        )}
                    </S.NoResultWrapper>
                );
            }

            return (
                <SecondPanel
                    loopTarget={loopTarget}
                    currentList={currentList}
                    rawList={currentList}
                    hasCheckbox={true}
                    hasCheckboxAll={false}
                    hasCategoryCheckboxAll={true}
                    hideCategoryCheckbox={false}
                    filterByCategory={({ category, itemCategory }) =>
                        category === itemCategory
                    }
                    getCategory={this.getCategory}
                    translator={(label) => label.name}
                    indexKey="id"
                    searchText={this.normalizedSearchQuery}
                    useEnhancedList={true}
                    disableSearchFilter={true}
                    onClickCheckBox={this.toggleLabel}
                    onUpdate={this.handleBulkToggleLabels}
                    isItemDisabled={(item) => Boolean(item.isBlock)}
                    categoryCheckedResolver={this.resolveCategoryChecked}
                    categoryIndeterminateResolver={
                        this.resolveCategoryIndeterminate
                    }
                    categoryNameGenerator={({ category }) =>
                        this.getCategoryName(category)
                    }
                    categoryContentRenderer={this.renderCategoryContent}
                    showEmptyCategories={true}
                    handleCategoryToggle={this.handleCategoryToggle}
                    categoryEditHandler={({ category }) =>
                        this.handleEditBreakdown(category)
                    }
                    defaultCategoryClosed={true}
                    reactiveListDebounceMs={0}
                    categoryMapDebounceMs={0}
                    emptyCategoryMessage={this.resolveEmptyCategoryMessage}
                    expandOnCheckAllClick={false}
                    ref="secondPanel"
                />
            );
        },
    },
    render() {
        const { handleCancel, handleSubmit } = this;

        return (
            <S.Wrapper>
                <S.SearchAndBreakdownWrapper>
                    {this.renderSearchInput()}
                    <WithDropdown
                        trigger={
                            <S.BreakdownBtn lang={this.language}>
                                <PlusIcon v-view-box />
                                {this.$t(
                                    'NEW_DASHBOARD.category_customBreakdown'
                                )}
                            </S.BreakdownBtn>
                        }
                        options={[
                            {
                                text: this.$t('NEW_DASHBOARD.manual_labeling'),
                                cb: () => {
                                    const data = {
                                        breakdownEditId: null,
                                        breakdownEditWidgetAssetIds: [],
                                        mode: 'manual',
                                        isTemplateView: this.isTemplateView,
                                        currentView: this.currentView,
                                    };
                                    this.setCustomBreakdownPanelData(data);
                                    this.postIframeMessage({
                                        type: IFRAME_EVENT_MESSAGE.TOGGLE_CUSTOM_BREAKDOWN_PANEL,
                                        origin: 'VUE',
                                        data,
                                    });
                                },
                            },
                            {
                                text: this.$t('NEW_DASHBOARD.auto_labeling'),
                                cb: () => {
                                    const data = {
                                        breakdownEditId: null,
                                        breakdownEditWidgetAssetIds: [],
                                        mode: 'auto',
                                        isTemplateView: this.isTemplateView,
                                        currentView: this.currentView,
                                    };
                                    this.setCustomBreakdownPanelData(data);
                                    this.postIframeMessage({
                                        type: IFRAME_EVENT_MESSAGE.TOGGLE_CUSTOM_BREAKDOWN_PANEL,
                                        origin: 'VUE',
                                        data,
                                    });
                                },
                            },
                        ]}
                    />
                </S.SearchAndBreakdownWrapper>
                <S.ListSpacer>{this.renderSecondPanelList()}</S.ListSpacer>

                <S.CTAWrapper>
                    <S.CancelActionButton onClick={handleCancel}>
                        {this.$t('COMMON.cancel')}
                    </S.CancelActionButton>
                    <ActionButton onClick={handleSubmit}>
                        {this.$t('COMMON.apply')}
                    </ActionButton>
                </S.CTAWrapper>
            </S.Wrapper>
        );
    },
};

export default withHMR(module, SecondPanelContent, SecondPanelContent.name);
